<template>
    <div>
        <media-only desktop>
            <table class="hotel-offers table table-striped table-bordered">
                <tbody>
                    <template v-for="(group, groupIndex) in groupedVariants">
                        <tr :key="'group' + groupIndex">
                            <td :colspan="showPrices ? 4 : 3" class="product-title">{{ group.title }}</td>
                        </tr>
                        <tr
                            v-for="(variant, index) in group.variants"
                            :key="group.title + groupIndex + 'variant' + index"
                        >
                            <td class="image-cell">
                                <nuxt-link :to="variant._productLink">
                                    <img
                                        :src="variant.productImage.medium"
                                        :alt="variant.productImage.altText"
                                        :title="variant.productImage.altText"
                                        class="img-fluid"
                                    />
                                </nuxt-link>
                            </td>
                            <td class="align-middle">
                                {{ variant.duration }}, {{ variant.persons }}, {{ variant.roomType }},
                                {{ variant.catering }}
                            </td>
                            <td v-if="showPrices" class="text-center align-middle price-cell">
                                <Price
                                    :minimum-per-night-price="variant.price.regularPriceValues.perPersonPerNightPrice"
                                    :minimumPerPersonPrice="variant.price.regularPriceValues.perPersonPrice"
                                    :minimumPrice="variant.price.regularPriceValues.price"
                                    class="offer-price font-weight-bold"
                                >
                                    <DiscountBadge
                                        v-if="variant.price.regularPriceValues.discountPercentage"
                                        :discount="variant.price.regularPriceValues.discountPercentage"
                                    ></DiscountBadge>
                                </Price>
                            </td>
                            <td class="text-center align-middle">
                                <nuxt-link :to="variant._productLink" class="btn btn-primary btn-lg">
                                    {{ $t('HotelOffers.toOffer') }}
                                </nuxt-link>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </media-only>

        <media-only mobile>
            <div class="hotel-offers">
                <div v-for="(group, index) in groupedVariants" :key="'group' + index" class="container">
                    <h3 class="product-title mobile">{{ group.title }}</h3>
                    <div
                        v-for="(variant, index) in group.variants"
                        :key="group.title + 'variant' + index"
                        class="product-card row align-items-center"
                    >
                        <nuxt-link :to="variant._productLink" class="col-6 col-sm-4">
                            <img
                                :src="variant.productImage.medium"
                                :alt="variant.productImage.altText"
                                :title="variant.productImage.altText"
                                class="img-fluid"
                            />
                        </nuxt-link>

                        <div class="col-6 col-sm-4">
                            {{ variant.duration }}, {{ variant.persons }}, {{ variant.roomType }},
                            {{ variant.catering }}
                        </div>

                        <div class="col-12 col-sm-4 bottom-row mt-2 mt-sm-0">
                            <Price
                                v-if="showPrices"
                                :minimum-per-night-price="getPerNightPrice(variant.price)"
                                :minimumPerPersonPrice="getPerPersonPrice(variant.price)"
                                :minimumPrice="getPrice(variant.price)"
                                :showPrefix="false"
                                class="offer-price font-weight-bold"
                            >
                                <DiscountBadge
                                    v-if="variant.price.regularPriceValues.discountPercentage"
                                    :discount="variant.price.regularPriceValues.discountPercentage"
                                ></DiscountBadge>
                            </Price>

                            <nuxt-link :to="variant._productLink" class="btn btn-primary">
                                {{ $t('HotelOffers.toOffer') }}
                            </nuxt-link>
                        </div>
                    </div>
                </div>
            </div>
        </media-only>
    </div>
</template>

<script>
import linkMixin from '@/mixins/getLink';
import Price from '@/components/Price';
import DiscountBadge from '@/components/DiscountBadge';

export default {
    name: 'HotelOffers',
    mixins: [linkMixin],
    components: {
        Price,
        DiscountBadge
    },
    props: {
        variants: Array,
        hotelWebname: {
            type: String,
            required: true
        }
    },
    computed: {
        groupedVariants() {
            return this.variants.reduce((groups, current) => {
                if (!groups[current.productId]) {
                    groups[current.productId] = {
                        title: current.productTitle,
                        variants: []
                    };
                }

                //Creating a copy in order to prevent store direct mutation by assigning a product link
                const variantShallowCopy = { ...current };
                variantShallowCopy._productLink = this.getLink(variantShallowCopy);
                groups[variantShallowCopy.productId].variants.push(variantShallowCopy);
                return groups;
            }, {});
        },
        showPrices() {
            return this.variants.some(
                ({ price }) =>
                    price && !!(this.getPrice(price) || this.getPerPersonPrice(price) || this.getPerNightPrice(price))
            );
        }
    },
    methods: {
        getLink(variant) {
            const { productId, productWebname } = variant;
            return this.getLinkByType(
                {
                    productId,
                    webnames: [this.hotelWebname, productWebname || this.toWebName(variant.productTitle)]
                },
                'product'
            );
        },
        toWebName(name) {
            return name.trim().toLowerCase().replace(/ /g, '-').replace(/:/g, '');
        },
        //Should be actually consistent with the Price model
        getPerPersonPrice(price) {
            return this.$getSafe(price, 'regularPriceValues.perPersonPrice');
        },
        getPerNightPrice(price) {
            return this.$getSafe(price, 'regularPriceValues.perPersonPerNightPrice');
        },
        getPrice(price) {
            return this.$getSafe(price, 'regularPriceValues.price');
        }
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/bootstrap/breakpoints' as breakpoints;

.hotel-offers {
    .product-title {
        font-weight: bold;

        &.mobile {
            font-size: 24px;
        }
    }

    td {
        padding: variables.$grid-padding;

        &.image-cell {
            width: 175px;
        }
    }

    .price-cell {
        min-width: 130px;

        .offer-price {
        }
    }

    .offer-price {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;

        ::v-deep .help-text {
            position: relative;
            letter-spacing: normal;
            bottom: 0;
            font-size: 1em;
        }

        @include breakpoints.media-breakpoint-down(variables.$mobile-breakpoint-max) {
            justify-content: flex-start;
            flex-direction: row;

            ::v-deep .discount-badge {
                margin-left: 5px;
            }

            & ~ a.btn {
                white-space: nowrap;
            }
        }
    }

    .product-card {
        box-shadow: 0 7px 18px 0 rgba(0, 0, 0, 0.1);
        padding: 10px;
        margin-bottom: variables.$grid-padding;
        display: flex;

        .bottom-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
    }
}
</style>
